import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing, Smartphone, Camera } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { PromptSource } from '@/hooks/useAppDownloadPrompt';

interface AppDownloadDialogProps {
  open: boolean;
  onDownload: (source: PromptSource) => void;
  onDismiss: () => void;
  onShown: (source: PromptSource) => void;
}

export function AppDownloadDialog({ open, onDownload, onDismiss, onShown }: AppDownloadDialogProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (open) onShown('modal');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const benefits = [
    { icon: BellRing, text: t('appDownload.benefitAlarms') },
    { icon: Smartphone, text: t('appDownload.benefitQuickAccess') },
    { icon: Camera, text: t('appDownload.benefitNative') },
  ];

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onDismiss(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('appDownload.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('appDownload.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-3">
          {benefits.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Icon className="h-4 w-4 text-primary" />
              </span>
              <span className="text-sm text-muted-foreground">{text}</span>
            </li>
          ))}
        </ul>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button className="w-full" onClick={() => onDownload('modal')}>
            {t('appDownload.download')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={onDismiss}>
            {t('appDownload.notNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
